/** @description 全局规则 form */
import {
  Kind,
  DataType,
  ConditionItem,
  NodeSelectItem,
} from '@/components/index';
import { useState, useMemo, useEffect, useImperativeHandle, useReducer } from 'react';
import { useSelector, useDispatch } from 'umi';
import _ from 'lodash';
import moment from 'moment';
import ConditionForm from './condition-form';
import OnTable from './on-table';
import { message } from 'antd';
import { Edge } from 'react-flow-renderer';

type CommonRuleProps = {
  onRef: React.Ref<unknown>;
};

const CommonRule: React.FC<CommonRuleProps> = ({ onRef }) => {
  useImperativeHandle(onRef, () => {
    return {
      onReceive,
      conditions,
      saveTableData,
      setConditions: (values: ConditionItem[]) => conditionsDispatch({ type: 'replace', values }),
    };
  });

  const [onReceive, setOnReceive] = useState<DataType[]>([]);
  const [condition, setCondition] = useState<string>('');
  const [nodeKey, setNodeKey] = useState<string | null>(null);
  const [conditions, conditionsDispatch] = useReducer(conditionsReducer, [])

  const nodeSelectList = useSelector((state: any) => state.open.nodeSelectList);
  const workflowConfig = useSelector((state: any) => state.open.workflowConfig);
  const edges = useSelector((state: any) => state.open.edges);
  const dispatch = useDispatch();

  /** @description 提取匹配规则列表 */
  useEffect(() => {
    let edges_with_label = edges.filter((e: Edge) => e.label).map((e: Edge) => e.label);
    let common_with_label = conditions.map((c: ConditionItem) => c.condition);
    let result = Array.from(new Set([ ...edges_with_label, ...common_with_label ])).map((vo: string) => { return { value: vo } });
    dispatch({ type: 'open/setRegexList', payload: result });
  }, [edges, conditions]);

  useEffect(() => {
    if (!workflowConfig) return;
    conditionsDispatch({ type: 'replace', values: workflowConfig.common_conditions });
    setOnReceive([...onReceive, ...workflowConfig.common_onReceive]);
  }, [workflowConfig]);

  /** 可选节点发生变化时，删除已失效的condition */
  useEffect(() => {
    const notExistIndex: number[] = [];
    const newConditions = [...conditions];
    newConditions.forEach((n: ConditionItem, index) => {
      if (!existNode.includes(n.key)) {
        notExistIndex.push(index);
      }
    });
    if (!notExistIndex.length) return;
    _.pullAt(newConditions, notExistIndex);
    conditionsDispatch({ type: 'replace', values: newConditions });
  }, [nodeSelectList]);

  const isOnReceiveMatchChar = useMemo(() => {
    return _.find(onReceive, function (o) {
      return o.code === '匹配字段';
    });
  }, [onReceive]);

  const existNode = useMemo(() => {
    return nodeSelectList.map((n: NodeSelectItem) => n.key);
  }, [nodeSelectList]);

  const saveTableData = (kind: Kind, value: DataType[]) => {
    setOnReceive(value);
  };

  const handleConditionDelete = (key: string) => {
    conditionsDispatch({ type: 'delete', key, condition: '' })
  };

  const replaceNodes = (newArr: ConditionItem[]) => conditionsDispatch({ type: 'replace', values: newArr });

  const addNodes = () => {
    if (!condition) return message.warning('匹配的字段不能为空！');
    const key = !nodeKey
      ? 'n-' + moment(new Date()).valueOf().toString()
      : nodeKey;
    const isValid =
      _.findIndex(
        conditions,
        (c) => c.condition === condition || c.key === key,
      ) === -1;
    if (!isValid) return message.warning('该匹配字段或节点已存在！');

    if (!nodeKey && dispatch) {
      dispatch({
        type: 'open/addNode',
        payload: {
          type: 'globalNode',
          source: '',
          condition,
          key,
        },
      });
    }

    conditionsDispatch({ type: 'add', key, condition })
    setNodeKey(null);
    setCondition('');
  };

  return (
    <>
      <h4>执行规则</h4>
      <OnTable
        id=""
        data={onReceive}
        kind="onReceive"
        saveTableData={saveTableData}
      />
      {isOnReceiveMatchChar && (
        <>
          <h4>匹配字段</h4>
          <ConditionForm
            conditions={conditions}
            nodeSelectList={nodeSelectList}
            currentNodeId=""
            currentNodeType="globalNode"
            nodeKey={nodeKey}
            setNodeKey={(e: string) => setNodeKey(e)}
            handleConditionDelete={handleConditionDelete}
            condition={condition}
            setCondition={(e: string) => setCondition(e)}
            addNodes={addNodes}
            replaceNodes={replaceNodes}
          />
        </>
      )}
    </>
  );
};

function conditionsReducer(conditions: ConditionItem[], action: { type: 'add' | 'delete' | 'replace', key?: string, condition?: string, values?: ConditionItem[] }): ConditionItem[] {
  switch(action.type) {
    case 'add': {
      return [
        ...conditions,
        {
          key: action.key,
          condition: action.condition
        }
      ] as ConditionItem[]
    }
    case 'delete': {
      return conditions.filter(c => c.key !== action.key)
    }
    default:
      // throw Error('未知 action: ' + action.type);
      return action?.values ? [...action?.values] : []
  }
}

export default CommonRule;
